// src/components/authentication/SignUp.js
/**
 * Sign-up screen for new users.
 *
 * Renders a form with:
 *  - email
 *  - password (with live requirements)
 *  - confirm-password
 *  - T&C checkbox
 *  - OPTIONAL "Have a code?" section for access codes
 *
 * Flow:
 *  1. Validate email / passwords / T&C.
 *  2. If a code is entered, validate it:
 *      - Firestore: access_codes/{CODE}
 *      - must exist
 *      - redeemed < redeemMax (or equivalent)
 *      - if invalid/expired → show popup and STOP (no signup).
 *  3. Link anon user or create new email+password user.
 *  4. Mark user doc as registered + onboarding step.
 *  5. If no plan exists, create a Free / freemium plan.
 *  6. If a code is entered, redeem it (increment redeemed + upgrade entitlements.plan).
 *  7. Redirect:
 *      - free/performance → /recording/forced-spirometry/guide
 *      - other tiers → /pay/checkout
 */

import React, { useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  getAuth,
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  linkWithCredential,
} from "firebase/auth";
import {
  getFirestore,
  doc,
  runTransaction,
  serverTimestamp,
  getDoc, // NEW: for pre-checking the code
} from "firebase/firestore";
import { firebaseApp } from "../../firebase/firebaseApp";
import { upsertUserDoc, migrateLegacyDocIfAny } from "../../utils/userDoc";
import { useUserDoc } from "../../hooks/useUserDoc";
import usePopup from "../../hooks/usePopup";
import ErrorPopup from "../ErrorPopup";
import { Checklist } from "./PasswordChecklist";
import { friendlyMessage } from "../../utils/authentication/errorMessages";
import { EMAIL_REGEX } from "../../utils/constants";
import usePasswordCriteria from "../../hooks/usePasswordCriteria";
import { useTranslation } from "react-i18next";

export default function SignUp({
  defaultEmail = "",
  lockEmail = false,
  showCodeField = false, // show "Have a code?" flow when true
}) {
  /* ---------------------------------------------------------------- */
  /* Hooks & state                                                    */
  /* ---------------------------------------------------------------- */
  const { t } = useTranslation();
  const { data } = useUserDoc(); // current user doc (contains pre-selected plan / entitlements)

  const auth = getAuth(firebaseApp);
  const db = getFirestore(firebaseApp);
  const navigate = useNavigate();
  const { popup, open: openPopup, close: closePopup } = usePopup();

  const [agreed, setAgreed] = useState(false);
  const [fields, setFields] = useState({
    email: defaultEmail || "",
    password: "",
    confirm: "",
    code: "", // optional access code
  });

  // keep in sync if defaultEmail arrives later
  React.useEffect(() => {
    if (defaultEmail) setFields((p) => ({ ...p, email: defaultEmail }));
  }, [defaultEmail]);

  const [showChecklist, setShowChecklist] = useState(false);
  const [viewPassword, setViewPassword] = useState(false);
  const [viewConfirmPassword, setViewConfirmPassword] = useState(false);

  const [showCodeInput, setShowCodeInput] = useState(false); // toggle for code input

  /**
   * Live password-requirement booleans produced by the custom hook.
   * `{ length, upper, lower, number, special }`
   */
  const criteria = usePasswordCriteria(fields.password, auth);

  /**
   * Update one of the text inputs (`email`, `password`, `confirm`, `code`).
   */
  const handleChange = (key) => (e) =>
    setFields((prev) => ({ ...prev, [key]: e.target.value }));

  /* ---------------------------------------------------------------- */
  /* Submit handler                                                   */
  /* ---------------------------------------------------------------- */
  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      closePopup();

      /* E-mail format */
      if (!EMAIL_REGEX.test(fields.email)) {
        openPopup({ message: t("signup.emailRegexError") });
        return;
      }

      /* Passwords match */
      if (fields.password !== fields.confirm) {
        openPopup({ message: t("signup.passwordMatchError") });
        return;
      }

      /* All password criteria true */
      if (!Object.values(criteria).every(Boolean)) {
        openPopup({ message: t("signup.passwordRequirementsError") });
        return;
      }

      /* Terms accepted */
      if (!agreed) {
        openPopup({
          message: t("signup.termsAcceptedError"),
        });
        return;
      }

      // ---------------------------------------------------------------------
      // PRE-CHECK CODE (if provided) – DO NOT CREATE ACCOUNT IF INVALID
      // ---------------------------------------------------------------------
      const rawCode = fields.code?.trim();
      let normalizedCode = null;

      if (rawCode) {
        normalizedCode = rawCode.toUpperCase();
        try {
          const codeRef = doc(db, "access_codes", normalizedCode);
          const snap = await getDoc(codeRef);

          if (!snap.exists()) {
            openPopup({
              message: t("signup.codeError", {
                defaultValue:
                  "We couldn't find that code. Please check it and try again.",
              }),
            });
            return;
          }

          const d = snap.data() || {};
          const redeemed = d.redeemed || 0;
          const redeemMax =
            d.redeemMax ?? d.redeemmax ?? d.maxRedemptions ?? d.maxRedeems ?? 0;

          // Any of these → treat as invalid / expired
          if (
            !Number.isFinite(redeemMax) ||
            redeemMax <= 0 ||
            redeemed >= redeemMax
          ) {
            openPopup({
              message: t("signup.codeError", {
                defaultValue:
                  "This code has expired or reached its usage limit. Please use a different code.",
              }),
            });
            return;
          }
        } catch (codeCheckErr) {
          openPopup({
            message: t("signup.codeError", {
              defaultValue:
                "We couldn't validate that code right now. Please try again.",
            }),
          });
          return;
        }
      }

      // ---------------------------------------------------------------------
      // CODE IS VALID (OR NOT PROVIDED) → PROCEED WITH SIGNUP
      // ---------------------------------------------------------------------
      try {
        /* Link or Create user */
        const current = auth.currentUser;
        if (current && current.isAnonymous) {
          // Link the anonymous user to email/password (keeps UID & data)
          const cred = EmailAuthProvider.credential(
            fields.email,
            fields.password
          );
          await linkWithCredential(current, cred);
        } else {
          // Fallback: no anon session — create a new account
          await createUserWithEmailAndPassword(
            auth,
            fields.email,
            fields.password
          );
        }

        // Mark user doc as registered & bump onboarding step
        const u = auth.currentUser;
        if (!u?.uid) {
          openPopup({
            message: t("signup.genericError", {
              defaultValue:
                "Something went wrong creating your account. Please try again.",
            }),
          });
          return;
        }

        await migrateLegacyDocIfAny(u.uid, fields.email); // safe no-op if none
        await upsertUserDoc(u.uid, {
          email: fields.email,
          accountStatus: "registered",
          onboarding: {
            step: "accountCreated",
            accountCreatedAt: serverTimestamp(),
          },
        });

        // -------------------------------------------------------------------
        // Ensure they have at least a Free / freemium plan if none exists
        // -------------------------------------------------------------------
        const existingPlanTier = (data?.plan?.tier || "").toLowerCase();
        const existingEntitlementPlan = (
          data?.entitlements?.plan || ""
        ).toLowerCase();
        const hasAnyPlan = !!(existingPlanTier || existingEntitlementPlan);

        if (!hasAnyPlan) {
          await upsertUserDoc(u.uid, {
            plan: {
              tier: "free",
              interval: null,
              kind: "freemium",
              currency: "GBP",
              price: 0,
              cadenceLabel: "",
              title: "Free",
              selectedAt: serverTimestamp(),
            },
            entitlements: {
              ...(data?.entitlements || {}),
              plan: "free",
            },
          });
        }

        // -------------------------------------------------------------------
        // Redeem code AFTER signup (we already know it's structurally valid)
        // -------------------------------------------------------------------
        let selectedTier = (
          existingPlanTier ||
          existingEntitlementPlan ||
          "free"
        ).toLowerCase();

        if (normalizedCode) {
          try {
            const codeRef = doc(db, "access_codes", normalizedCode);

            const codeData = await runTransaction(db, async (txn) => {
              const snap = await txn.get(codeRef);
              if (!snap.exists()) {
                throw new Error("code/not-found");
              }
              const d = snap.data() || {};
              const redeemed = d.redeemed || 0;
              const redeemMax =
                d.redeemMax ??
                d.redeemmax ??
                d.maxRedemptions ??
                d.maxRedeems ??
                0;

              if (!Number.isFinite(redeemMax) || redeemMax <= 0) {
                throw new Error("code/no-capacity");
              }
              if (redeemed >= redeemMax) {
                throw new Error("code/max-redeemed");
              }

              txn.update(codeRef, {
                redeemed: redeemed + 1,
                lastRedeemedAt: serverTimestamp(),
              });

              return { id: snap.id, ...d };
            });

            const upgradePlan = (codeData.plan || "performance").toLowerCase();

            await upsertUserDoc(u.uid, {
              entitlements: {
                ...(data?.entitlements || {}),
                plan: upgradePlan,
                source: "access_code",
                accessCodeId: normalizedCode,
                upgradedAt: serverTimestamp(),
              },
            });

            selectedTier = upgradePlan;
          } catch (codeErr) {
            // Extremely rare race / network case:
            // account is already created at this point, but code redeem failed.
            // Show a softer message but keep the account.
            openPopup({
              message: t("signup.codeError", {
                defaultValue:
                  "We created your account, but couldn't apply that code. Please contact support if this keeps happening.",
              }),
            });
          }
        }

        // -------------------------------------------------------------------
        // Redirect:
        //  - free or performance → /recording/forced-spirometry/guide
        //  - other (future paid tiers) → /pay/checkout
        // -------------------------------------------------------------------
        const finalTier = (selectedTier || "free").toLowerCase();
        if (finalTier === "free" || finalTier === "performance") {
          navigate("/recording/forced-spirometry/guide", { replace: true });
        } else {
          navigate("/pay/checkout", { replace: true });
        }
      } catch (err) {
        // If email is already in use, nudge to login instead of losing anon data
        if (
          err?.code === "auth/credential-already-in-use" ||
          err?.code === "auth/email-already-in-use"
        ) {
          openPopup({
            message:
              t("signup.emailInUse", {
                defaultValue:
                  "This email is already registered. Please sign in instead.",
              }) +
              " " +
              t("signup.tryLogin", {
                defaultValue: "Use the login link above.",
              }),
          });
          return;
        }
        openPopup({ message: friendlyMessage(err.code, err.message) });
      }
    },
    [
      fields,
      criteria,
      agreed,
      auth,
      db,
      navigate,
      openPopup,
      closePopup,
      data?.plan?.tier,
      data?.entitlements?.plan,
      t,
    ]
  );

  /* ---------------------------------------------------------------- */
  /* Render                                                           */
  /* ---------------------------------------------------------------- */
  return (
    <div className="relative flex flex-col items-stretch justify-end md:justify-center h-full w-full gap-4">
      {popup && <ErrorPopup popup={popup} onClose={closePopup} />}

      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-2 md:gap-4"
        noValidate
      >
        {/* Optional code section */}
        {showCodeField && (
          <div className="flex flex-col gap-2">
            <button
              type="button"
              className="text-xs text-blue-400 underline self-start"
              onClick={() => setShowCodeInput((v) => !v)}
            >
              {showCodeInput
                ? t("signup.hideCode", { defaultValue: "Hide code" })
                : t("signup.haveCode", { defaultValue: "Have a code?" })}
            </button>

            {showCodeInput && (
              <input
                type="text"
                autoComplete="off"
                placeholder={t("signup.codePlaceholder", {
                  defaultValue: "Enter access code (optional)",
                })}
                className="custom-input-on-dark w-full"
                value={fields.code}
                onChange={handleChange("code")}
              />
            )}
          </div>
        )}

        {/* email */}
        <input
          type="email"
          autoComplete="username"
          placeholder={t("signup.email")}
          className={`custom-input-on-dark w-full ${
            lockEmail ? "opacity-70 cursor-not-allowed" : ""
          }`}
          value={fields.email}
          onChange={lockEmail ? undefined : handleChange("email")}
          readOnly={lockEmail}
          required
        />

        {/* password + checklist */}
        <div className="relative flex flex-row gap-2">
          <input
            type={viewPassword ? "text" : "password"}
            autoComplete="new-password"
            placeholder={t("signup.password")}
            className="custom-input-on-dark w-full"
            value={fields.password}
            onChange={handleChange("password")}
            onFocus={() => setShowChecklist(true)}
            onBlur={() => setShowChecklist(false)}
            required
          />
          {showChecklist && <Checklist t={t} criteria={criteria} />}
          <button
            type="button"
            onClick={() => setViewPassword((v) => !v)}
            className="input-toggle-button-on-dark flex items-center !p-0 "
            aria-label={viewPassword ? "Hide password" : "Show password"}
            aria-pressed={viewPassword}
          >
            {/* Password visibility toggle */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className=" w-8 mx-2 input-icon"
              viewBox="0 -960 960 960"
            >
              {viewPassword ? (
                <path d="M644-428l-58-58q9-47-27-88t-93-32l-58-58q17-8 34.5-12t37.5-4q75 0 127.5 52.5T660-500q0 20-4 37.5T644-428zm128 126l-58-56q38-29 67.5-63.5T832-500q-50-101-143.5-160.5T480-720q-29 0-57 4t-55 12l-62-62q41-17 84-25.5t90-8.5q151 0 269 83.5T920-500q-23 59-60.5 109.5T772-302zm20 246L624-222q-35 11-70.5 16.5T480-200q-151 0-269-83.5T40-500q21-53 53-98.5t73-81.5L56-792l56-56 736 736-56 56zM222-624q-29 26-53 57t-41 67q50 101 143.5 160.5T480-280q20 0 39-2.5t39-5.5l-36-38q-11 3-21 4.5t-21 1.5q-75 0-127.5-52.5T300-500q0-11 1.5-21t4.5-21l-84-82zm319 93zm-151 75z"></path>
              ) : (
                <path d="M480-320q75 0 127.5-52.5T660-500q0-75-52.5-127.5T480-680q-75 0-127.5 52.5T300-500q0 75 52.5 127.5T480-320zm0-72q-45 0-76.5-31.5T372-500q0-45 31.5-76.5T480-608q45 0 76.5 31.5T588-500q0 45-31.5 76.5T480-392zm0 192q-146 0-266-81.5T40-500q54-137 174-218.5T480-800q146 0 266 81.5T920-500q-54 137-174 218.5T480-200zm0-300zm0 220q113 0 207.5-59.5T832-500q-50-101-144.5-160.5T480-720q-113 0-207.5 59.5T128-500q50 101 144.5 160.5T480-280z"></path>
              )}
            </svg>
          </button>
        </div>

        {/* confirm */}
        <div className="flex flex-row gap-2">
          <input
            type={viewConfirmPassword ? "text" : "password"}
            autoComplete="new-password"
            placeholder={t("signup.confirmPassword")}
            className="custom-input-on-dark w-full"
            value={fields.confirm}
            onChange={handleChange("confirm")}
            required
          />
          <button
            type="button"
            onClick={() => setViewConfirmPassword((v) => !v)}
            className="input-toggle-button-on-dark flex items-center !p-0 "
            aria-label={viewConfirmPassword ? "Hide password" : "Show password"}
            aria-pressed={viewConfirmPassword}
          >
            {/* Confirm Password visibility toggle */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className=" w-8 mx-2 input-icon"
              viewBox="0 -960 960 960"
            >
              {viewConfirmPassword ? (
                <path d="M644-428l-58-58q9-47-27-88t-93-32l-58-58q17-8 34.5-12t37.5-4q75 0 127.5 52.5T660-500q0 20-4 37.5T644-428zm128 126l-58-56q38-29 67.5-63.5T832-500q-50-101-143.5-160.5T480-720q-29 0-57 4t-55 12l-62-62q41-17 84-25.5t90-8.5q151 0 269 83.5T920-500q-23 59-60.5 109.5T772-302zm20 246L624-222q-35 11-70.5 16.5T480-200q-151 0-269-83.5T40-500q21-53 53-98.5t73-81.5L56-792l56-56 736 736-56 56zM222-624q-29 26-53 57t-41 67q50 101 143.5 160.5T480-280q20 0 39-2.5t39-5.5l-36-38q-11 3-21 4.5t-21 1.5q-75 0-127.5-52.5T300-500q0-11 1.5-21t4.5-21l-84-82zm319 93zm-151 75z"></path>
              ) : (
                <path d="M480-320q75 0 127.5-52.5T660-500q0-75-52.5-127.5T480-680q-75 0-127.5 52.5T300-500q0 75 52.5 127.5T480-320zm0-72q-45 0-76.5-31.5T372-500q0-45 31.5-76.5T480-608q45 0 76.5 31.5T588-500q0 45-31.5 76.5T480-392zm0 192q-146 0-266-81.5T40-500q54-137 174-218.5T480-800q146 0 266 81.5T920-500q-54 137-174 218.5T480-200zm0-300zm0 220q113 0 207.5-59.5T832-500q-50-101-144.5-160.5T480-720q-113 0-207.5 59.5T128-500q50 101 144.5 160.5T480-280z"></path>
              )}
            </svg>
          </button>
        </div>

        {/* T&C/Privacy checkbox */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="terms"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            required
          />
          <label htmlFor="terms" className="text-xs">
            {t("signup.agree1")}
            <Link
              to={`https://www.breathe-ultra.com/terms-and-conditions`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 underline"
            >
              {t("signup.termsConditions")}
            </Link>{" "}
            {t("signup.agree2")}
            <Link
              to={`https://www.breathe-ultra.com/privacy-policy`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 underline"
            >
              {t("signup.privacy")}
            </Link>
          </label>
        </div>

        <button type="submit" className="primary-button w-full">
          {t("signup.cta")}
        </button>
        <Link to="/auth/login" className="link w-full text-center">
          {t("signup.login")}
        </Link>
      </form>
    </div>
  );
}
